const { formatMoney, getElements, getElement } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  lineItemProperties: '[data-properties]',
  estimatedDelivery: '[data-product-estimated-delivery]',
  estimatedDispatch: '[data-product-estimated-dispatch]',
  estimatedDispatchText: '[data-product-estimated-dispatch-text]',
  productVariantNameReplace: '[data-product-variant-name-replace]',
}

const contextSelectors = {
  quickBuy: '.qb-Detail[data-context="quick-buy"]',
  other:
    ':is(.prd-Detail, .prd-SizeGuide, .qb-Detail[data-context="product-quick-buy"])',
}

class Variant {
  constructor (data, context) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data

    /**
     * @type {string}
     * @public
     */
    this.context = context
    this.contextSelectors =
      this.context === 'quick-buy'
        ? contextSelectors.quickBuy
        : contextSelectors.other
  }

  setPrice () {
    unhidePriceEls(this.contextSelectors)

    const priceElements = getElements(
      `${this.contextSelectors} ${selectors.price}`,
    )
    priceElements.forEach(el => (el.innerHTML = formatMoney(this.data.price)))

    const priceCompareElements = getElements(
      `${this.contextSelectors} ${selectors.priceCompare}`,
    )
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements(this.contextSelectors)
    }
  }

  setAvailability () {
    const renderBis = Boolean(
      getElement(
        `[data-available="false"][data-bis-available="true"][data-variant-id='${this.id}']`,
      ),
    )

    let buttonText = this.data.available
      ? window.theme.strings.addToCart
      : window.theme.strings.soldOut

    if (renderBis) {
      buttonText = window.theme.strings.notifyMe
    }

    getElements(`${this.contextSelectors} ${selectors.addToCart}`).forEach(
      el => {
        const quickBuyButton = !!el.closest('quick-buy')

        el.disabled = !this.data.available && !renderBis
        el.setAttribute('data-bis', renderBis)

        if (this.context === 'quick-buy') {
          if (renderBis) {
            el.setAttribute('data-drawers-trigger', 'bis')
          } else {
            el.removeAttribute('data-drawers-trigger')
          }
        }

        if (this.context !== 'product') {
          el.setAttribute('data-variant-title', this.data.title)
          el.setAttribute('data-variant-id', this.data.id)
          if (quickBuyButton && renderBis) {
            el.setAttribute('data-quick-buy-external-el', 'trigger')
            el.setAttribute('data-section-name', 'quick-buy-bis')
          } else if (quickBuyButton) {
            el.removeAttribute('data-quick-buy-external-el')
            el.removeAttribute('data-section-name')
          }
        }
      },
    )

    getElements(`${this.contextSelectors} ${selectors.addToCartText}`).forEach(
      el => (el.textContent = buttonText),
    )
  }

  setNameReplace () {
    const nameReplaceElements = getElements(
      `${selectors.productVariantNameReplace}`,
    )
    if (nameReplaceElements.length > 0) {
      nameReplaceElements.forEach(el => {
        el.textContent = this.data.title
      })
    }
  }

  updateInput () {
    const inputSelectors = `${this.contextSelectors} form[action*="/cart/add"] [name="id"]`
    getElements(inputSelectors).forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setUrl () {
    if (this.context === 'quick-buy') return

    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    if (this.context === 'quick-buy') return

    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {string}
     * @public
     */
    this.context = this.dataset.context
    this.contextSelectors =
      this.context === 'quick-buy'
        ? contextSelectors.quickBuy
        : contextSelectors.other

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant, this.context)]
  }

  setupListeners () {
    super.setupListeners()

    this.onCartChanged = this.onCartChanged.bind(this)
    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    this.afterUpdateItemQuantity = this.afterUpdateItemQuantity.bind(this)
    this.afterUpdateRemoveItem = this.afterUpdateRemoveItem.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
      this.afterUpdateItemQuantity,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.afterUpdateRemoveItem,
    )
  }

  async onVariantChange (e) {
    const variant = this.getVariant()

    this.dispatchVariantChangeEvent(e, variant)

    variant.setAvailability()
    variant.setPrice()
    variant.setNameReplace()
    variant.updateInput()

    if (!this.currentVariant && this.context !== 'quick-buy') {
      this.resetPage(variant)
      return
    }

    variant.setUrl()
    variant.setShareUrl()

    this.updateOtherVariantSelectors(variant)
    this.updateButtonState()
    this.updateProperties(e)
  }

  updateProperties (e) {
    const showPreview = e?.target?.dataset?.showPreview === 'true'

    if (showPreview) {
      const currentValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--ProductDetail_Height_extra')
        .trim()
      if (!currentValue || currentValue === '0px') {
        document.documentElement.style.setProperty(
          '--ProductDetail_Height_extra',
          '25px',
        )
      }
    } else {
      document.documentElement.style.removeProperty(
        '--ProductDetail_Height_extra',
      )
    }
  }

  dispatchVariantChangeEvent (event, variant) {
    this.dispatchEvent(
      new CustomEvent(window.BAO.EVENTS.VARIANT.CHANGED, {
        detail: {
          event,
          variant,
        },
        bubbles: true,
      }),
    )
  }

  async onCartChanged () {
    const cartData = await window.BAO.Utils.fetchJSON(`/cart.json`, {
      method: 'GET',
    })
    if (!cartData) return

    this.updateInBagCounts(cartData)
  }

  updateInBagCounts (cartData) {
    const countEls = document.querySelectorAll('[data-variant-count-id]')
    if (!countEls || !cartData) return

    countEls.forEach(el => {
      const id = el.getAttribute('data-variant-count-id')
      const item = cartData.items.find(item => {
        return item.variant_id.toString() === id.toString()
      })

      el.innerHTML = item
        ? `${item.quantity} ${window.theme.strings.inBag}`
        : ``
    })
  }

  afterUpdateItemQuantity (e) {
    const variantId = e.detail.id
    const variantQuantity = e.detail.quantity

    const potentialVariantCount = document.querySelector(
      `[data-variant-count-id='${variantId}']`,
    )

    if (!potentialVariantCount) return

    potentialVariantCount.innerHTML =
      variantQuantity === 0
        ? ''
        : `${variantQuantity} ${window.theme.strings.inBag}`
  }

  afterUpdateRemoveItem (e) {
    const variantId = e.detail.id

    const potentialVariantCount = document.querySelector(
      `[data-variant-count-id='${variantId}']`,
    )

    if (!potentialVariantCount) return

    potentialVariantCount.innerHTML = ``
  }

  updateButtonState () {
    if (this.context === 'quick-buy') {
      document
        .querySelector(
          '.qb-Detail[data-context="quick-buy"] .qb-DetailVariants_Options',
        )
        .classList.remove('qb-DetailVariants_Options-noneSelected')
    } else {
      document
        .querySelector('.prd-DetailVariants_Options')
        .classList.remove('prd-DetailVariants_Options-noneSelected')
      document
        .querySelector(
          '.qb-Detail[data-context="product-quick-buy"] .qb-DetailVariants_Options',
        )
        ?.classList.remove('qb-DetailVariants_Options-noneSelected')
    }
  }

  updateOtherVariantSelectors (variant) {
    if (this.context === 'product-quick-buy') {
      const variantId = variant.data.id
      const matchingVariantRadioEl = Array.from(
        document.querySelectorAll(
          '.prd-Detail [data-variant-radios-el="option-choice"]',
        ),
      ).find(el => el.getAttribute('data-variant-id') === String(variantId))

      if (matchingVariantRadioEl && matchingVariantRadioEl.checked === false) {
        matchingVariantRadioEl.checked = true
      }
    }
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    if (this.context === 'quick-buy') return

    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this.dataset.context)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  setupListeners () {
    super.setupListeners()

    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onOptionClick = this.onOptionClick.bind(this)

    if (this.els?.trigger?.exists) {
      this.els.trigger.elements.forEach(triggerEl => {
        this.listeners.add(triggerEl, 'click', this.onTriggerClick)
      })

      if (this.els?.['option-choice']?.exists) {
        this.els['option-choice'].elements.forEach(optionEl => {
          this.listeners.add(optionEl, 'click', this.onOptionClick)
        })
      }

      document.querySelectorAll('.prd-Detail, .qb-Detail').forEach(element => {
        this.listeners.add(element, 'click', this.onCloseOptionClick)
      })
    }
  }

  onCloseOptionClick (e) {
    if (e.target.closest('[data-variant-radios-el="option"]')) return

    if (e.target.closest('[data-product-form]')) return

    document
      .querySelectorAll('[data-variant-radios-el="option"]')
      .forEach(el => {
        el.classList.remove(
          'prd-DetailVariants_Rows-expanded',
          'qb-DetailVariants_Rows-expanded',
        )
      })
  }

  onOptionClick (e) {
    const closestFieldset = e.currentTarget.closest(
      '[data-variant-radios-el="option"]',
    )

    if (!closestFieldset) return

    const triggerName = closestFieldset.querySelector(
      '[data-variant-radios-el="trigger-name"]',
    )

    if (!triggerName) return

    triggerName.innerHTML = e.currentTarget.value
  }

  onTriggerClick (e) {
    e.currentTarget
      .closest('[data-variant-radios-el="option"]')
      .classList.toggle(
        this.context === 'product'
          ? 'prd-DetailVariants_Rows-expanded'
          : 'qb-DetailVariants_Rows-expanded',
      )
  }

  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls (contextSelectors) {
  unhideElements(
    getElements(
      `${contextSelectors} ${selectors.price}, ${contextSelectors} ${selectors.priceCompare}`,
    ),
  )
}

function hidePriceCompareElements (contextSelectors) {
  hideElements(getElements(`${contextSelectors} ${selectors.priceCompare}`))
}
